import { useEffect, useCallback, useContext, useMemo } from "react";
import { getWebViewInfo } from "@b2bportal/core-utilities";
import type { RbcPartnerMetadata } from "@b2bportal/rbc-accounts-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  USER_CAMPAIGN_KEY,
  USER_CONTENT_KEY,
  USER_MEDIUM_KEY,
  USER_SOURCE_KEY,
} from "@hopper-b2b/utilities";
import { UserContext } from "@/App";
import { AppTrackingContext } from "@/components/AppTrackingContext";
import { postToNativeBridge } from "@/utils/nativeBridge";
import {
  type RBCAnalyticsEvent,
  type RBCUniversalEventParameters,
  createRBCEvent,
  getSearchTermFromURL,
} from "@/utils/rbcAnalytics";

declare global {
  interface Window {
    dataLayer: Array<RBCAnalyticsEvent>;
  }
}

export const usePublishRBCEvent = () => {
  const { language } = useI18nContext();
  const { sessionInfo } = useContext(UserContext);
  const { trackers } = useContext(AppTrackingContext);
  const rbcData = sessionInfo?.userInfo?.partnerMetadata as
    | RbcPartnerMetadata
    | undefined;
  const webviewInfo = useMemo(() => getWebViewInfo(), []);

  useEffect(() => {
    if (typeof window !== "undefined" && !window.dataLayer) {
      window.dataLayer = [];
    }
  }, []);

  const pushToDataLayer = useCallback(
    (eventName: string, properties?: any) => {
      if (typeof window === "undefined" || !window.dataLayer) {
        console.warn("Google Analytics dataLayer not found.");
        return;
      }

      const universalEventParameters: RBCUniversalEventParameters = {
        loyalty_surrogate_id: rbcData?.tenantUserId,
        confirmation_id: properties?.confirmation_id,
        value: properties?.value,
        search_term: properties?.search_term || getSearchTermFromURL(),
        utm_source: trackers[USER_SOURCE_KEY],
        utm_medium: trackers[USER_MEDIUM_KEY],
        utm_campaign: trackers[USER_CAMPAIGN_KEY],
        utm_content: trackers[USER_CONTENT_KEY],
      };

      const eventData = createRBCEvent(
        eventName,
        "1.2",
        language,
        universalEventParameters,
        properties
      );

      // Only push events that match RBC patterns (clicked/tapped/viewed/page_view)
      if (eventData) {
        window.dataLayer.push(eventData);

        // If in webview, also post to native bridge
        if (webviewInfo.isWebView) {
          postToNativeBridge(eventName, language, {
            ...properties,
            loyalty_surrogate_id: rbcData?.tenantUserId,
            confirmation_id: universalEventParameters.confirmation_id,
            value: universalEventParameters.value,
            search_term: universalEventParameters.search_term,
            utm_source: universalEventParameters.utm_source,
            utm_medium: universalEventParameters.utm_medium,
            utm_campaign: universalEventParameters.utm_campaign,
            utm_content: universalEventParameters.utm_content,
          });
        }
      }
    },
    [language, rbcData?.tenantUserId, webviewInfo, trackers]
  );

  return pushToDataLayer;
};
